package com.partejar.app.core.ui.domain.address

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.text.input.KeyboardType
import com.partejar.app.core.address.CepDataSource
import com.partejar.app.core.address.withCepResult
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteAddress
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import org.koin.compose.koinInject

@Composable
fun PatientAddressFields(
    address: GestanteAddress,
    onAddressChange: (GestanteAddress) -> Unit,
    enabled: Boolean,
    source: CepDataSource = koinInject()
) {
    val currentAddress by rememberUpdatedState(address)
    val updateAddress by rememberUpdatedState(onAddressChange)
    var requestedCep by remember { mutableStateOf<String?>(null) }
    var retry by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(requestedCep, retry, enabled) {
        loading = false
        error = null
        val cep = requestedCep ?: return@LaunchedEffect
        if (!enabled || cep.length != 8) return@LaunchedEffect
        delay(350)
        val start = currentAddress
        if (start.cep != cep) return@LaunchedEffect
        loading = true
        try {
            val result = source.lookup(cep)
            currentCoroutineContext().ensureActive()
            if (result == null) {
                error = "CEP não encontrado. Você pode preencher o endereço manualmente."
            } else {
                updateAddress(currentAddress.withCepResult(start, result))
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Exception) {
            error = "Não foi possível consultar o CEP. Você pode preencher o endereço manualmente."
        } finally {
            loading = false
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
        PartejarTextField(
            address.cep.orEmpty(),
            {
                val cep = it.filter { char -> char in '0'..'9' }.take(8)
                if (cep != address.cep.orEmpty()) {
                    onAddressChange(address.copy(cep = cep))
                    requestedCep = cep
                    error = null
                }
            },
            label = "CEP",
            enabled = enabled,
            isError = error != null,
            supportingText = if (loading) "Consultando CEP…" else error ?: "Preencha 8 dígitos para buscar o endereço.",
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
        )
        if (error != null) {
            PartejarButton("Consultar CEP novamente", { retry++ }, enabled = enabled,
                variant = PartejarButtonVariant.TERTIARY)
        }
        PartejarTextField(address.street.orEmpty(), { onAddressChange(address.copy(street = it)) }, label = "Logradouro", enabled = enabled)
        PartejarTextField(address.number.orEmpty(), { onAddressChange(address.copy(number = it)) }, label = "Número", enabled = enabled)
        PartejarTextField(address.complement.orEmpty(), { onAddressChange(address.copy(complement = it)) }, label = "Complemento", enabled = enabled)
        PartejarTextField(address.neighborhood.orEmpty(), { onAddressChange(address.copy(neighborhood = it)) }, label = "Bairro", enabled = enabled)
        PartejarTextField(address.city.orEmpty(), { onAddressChange(address.copy(city = it)) }, label = "Cidade", enabled = enabled)
        PartejarTextField(address.state.orEmpty(), { onAddressChange(address.copy(state = it.uppercase().take(2))) }, label = "UF", enabled = enabled)
    }
}
